
import React, { FC, useContext, useEffect, useRef } from 'react';
import { Drawer, DrawerProps } from 'antd';
import classNames from 'classnames';
import { MobXProviderContext, observer } from 'mobx-react';
import uuid from 'uuid';
import { SIDE_DRAWER_WIDTH } from 'src/constants';
import type UiStore from 'src/stores/ui-store';

const clsPrefix = 'app-side-drawer';

type DrawerData = Pick<DrawerProps, 'visible' | 'onClose'>;

/**
 * Stores instances of widget drawers globally, for keeping no more than one of them visible
 */
const drawers = new Map<string, DrawerData>();

export interface SideDrawerProps extends DrawerProps {}

const SideDrawer: FC<SideDrawerProps> = observer((props) => {
  const { className, visible, onClose, ...otherProps } = props;
  const { ui } = useContext(MobXProviderContext) as { ui: UiStore };
  const idRef = useRef(uuid.v4());

  // Register component props
  useEffect(() => {
    drawers.set(idRef.current, { visible, onClose });
    return () => {
      drawers.delete(idRef.current);
    };
  }, []);

  // Syncing component props
  useEffect(() => {
    drawers.set(idRef.current, { visible, onClose });
  }, [visible, onClose]);

  // Watch opening of current drawer, then close any open drawers
  useEffect(() => {
    if (visible) {
      drawers.forEach((value, key) => {
        if (key !== idRef.current && value.visible) {
          value.onClose?.(null);
        }
      });
      ui?.setDrawerOpen(true);
    } else {
      let anyDrawerOpened = false;
      drawers.forEach((value) => {
        anyDrawerOpened = anyDrawerOpened || value.visible;
      });
      if (!anyDrawerOpened) {
        ui?.setDrawerOpen(false);
      }
    }
  }, [visible]);

  if (!ui) {
    return null;
  }
  const { isEmbedded, totalTopBarHeight } = ui;
  const drawerOffset = isEmbedded ? '0px' : totalTopBarHeight;
  return (
    <Drawer
      width={ui.isMobileSize ? '100vw' : SIDE_DRAWER_WIDTH}
      style={
        ui.isMobileSize
          ? undefined
          : {
              top: drawerOffset,
              height: `calc(100% - ${drawerOffset})`,
            }
      }
      className={classNames(clsPrefix, className)}
      mask={false}
      {...{
        visible,
        onClose,
        ...otherProps,
      }}
    />
  );
});

export default SideDrawer;
